import { useRef, useState } from 'react';
import { FloraAIIcon } from '@practium/ui';
import styles from './FloraUploadZone.module.css';

export interface FloraUploadZoneProps {
	label: string;
	onFileSelect?: (file: File) => void;
	onFilesSelect?: (files: File[]) => void;
	disabled?: boolean;
	loading?: boolean;
	loadingMessage?: string;
	blocked?: boolean;
	blockedMessage?: string;
}

export const FloraUploadZone = ({
	label,
	onFileSelect,
	onFilesSelect,
	disabled,
	loading = false,
	loadingMessage = 'A Flora está lendo o laudo...',
	blocked = false,
	blockedMessage = 'Salve ou cancele as análises para importar outro laudo.',
}: FloraUploadZoneProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);

	const isBlocked = disabled || loading || blocked;

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (isBlocked) return;

		const files = Array.from(e.target.files ?? []);
		if (files.length > 0 && onFilesSelect) onFilesSelect(files);
		else if (files[0]) onFileSelect?.(files[0]);

		e.target.value = '';
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();

		if (isBlocked) return;

		setIsDragging(false);
		const files = Array.from(e.dataTransfer.files ?? []);
		if (files.length > 0 && onFilesSelect) onFilesSelect(files);
		else if (files[0]) onFileSelect?.(files[0]);
	};

	if (loading) {
		return (
			<div className={`${styles.zone} ${styles.loadingZone}`} aria-busy="true" role="status">
				<FloraAIIcon className={`${styles.icon} ${styles.iconPulsing}`} />
				<span className={styles.loadingMessage}>{loadingMessage}</span>
				<span className={styles.subtitle}>A análise pode demorar alguns minutos </span>
			</div>
		);
	}

	if (blocked) {
		return (
			<div className={`${styles.zone} ${styles.loadingZone}`} role="status">
				<FloraAIIcon className={`${styles.icon} ${styles.iconBlocked}`} />
				<span className={styles.loadingMessage}>Suas análises foram carregadas</span>
				<span className={styles.subtitle}>{blockedMessage}</span>
			</div>
		);
	}

	return (
		<div
			className={`${styles.zone} ${isDragging ? styles.dragging : ''}`}
			onClick={() => {
				if (!isBlocked) inputRef.current?.click();
			}}
			onDragOver={(e) => {
				e.preventDefault();
				if (!isBlocked) setIsDragging(true);
			}}
			onDragLeave={() => setIsDragging(false)}
			onDrop={handleDrop}
			role="button"
			tabIndex={isBlocked ? -1 : 0}
			aria-disabled={isBlocked}
			onKeyDown={(e) => {
				if (isBlocked) return;
				if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
			}}
		>
			<input
				ref={inputRef}
				type="file"
				accept=".pdf,.xls,.xlsx"
				multiple
				className={styles.hiddenInput}
				onChange={handleFileChange}
				disabled={isBlocked}
			/>
			<FloraAIIcon className={styles.icon} />
			<span className={styles.title}>{label}</span>
			<span className={styles.subtitle}>
				A Flora preencherá automaticamente! Revise os campos antes de salvar
			</span>
		</div>
	);
};
